import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Scale, Sparkles } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import soilService from '../../services/soil';
import { errorMessage } from '../../utils/apiResponse';
import { cn } from '../../utils/cn';
import { REMOVAL_NUTRIENTS } from '../../utils/soilAnalytes';
import LeafStandardsEditor from '../../components/orchard/LeafStandardsEditor';
import type { CropRemoval, RemovalRates, RemovalResearch } from '../../types/soil';

const inputCls = 'w-full rounded-lg border border-neutral-300 px-2 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-primary-500';

function sourceFrom(r: RemovalResearch): string {
  const urls = r.sources.map((s) => s.url).join(' ');
  return [`Researched by AI and reviewed.`, r.basis, urls].filter(Boolean).join(' ').slice(0, 2000);
}

function CropCard({ crop, open, onSaved }: { crop: CropRemoval; open: boolean; onSaved: (c: CropRemoval) => void }) {
  const toText = (rates: RemovalRates) =>
    Object.fromEntries(REMOVAL_NUTRIENTS.map((n) => [n.key, rates[n.key] != null ? String(rates[n.key]) : '']));
  const [values, setValues] = useState<Record<string, string>>(() => toText(crop.rates));
  const [source, setSource] = useState(crop.source ?? '');
  const [research, setResearch] = useState<RemovalResearch | null>(null);
  const [busy, setBusy] = useState<'save' | 'research' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const bad = REMOVAL_NUTRIENTS.filter((n) => values[n.key].trim() !== '' && !(Number(values[n.key]) >= 0));

  const runResearch = async () => {
    setBusy('research');
    setError(null);
    try {
      const res = await soilService.researchRemoval(crop.crop_type_id);
      if (!res.success || !res.data) throw res;
      setResearch(res.data);
      if (res.data.found) {
        setValues(toText(res.data.rates));
        setSource(sourceFrom(res.data));
      }
    } catch (e) {
      setError(errorMessage(e, 'The research did not finish'));
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    if (bad.length) { setError(`Not a number: ${bad.map((n) => n.label).join(', ')}.`); return; }
    setBusy('save');
    setError(null);
    setSaved(false);
    try {
      const rates: RemovalRates = {};
      for (const n of REMOVAL_NUTRIENTS) if (values[n.key].trim() !== '') rates[n.key] = Number(values[n.key]);
      const res = await soilService.setRemoval(crop.crop_type_id, rates, source.trim() || null);
      if (!res.success || !res.data) throw res;
      onSaved(res.data);
      setResearch(null);
      setSaved(true);
    } catch (e) {
      setError(errorMessage(e, 'Could not save the figures'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className={cn(open && 'ring-2 ring-primary-300')}>
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-neutral-900 break-words">
              {crop.parent_name ? <span className="text-neutral-500 font-normal">{crop.parent_name} › </span> : null}{crop.name}
            </h3>
            <p className="text-xs text-neutral-500">
              {crop.areas === 1 ? '1 planting' : `${crop.areas} plantings`}
              {Object.keys(crop.rates).length === 0 && ' · no removal figures yet'}
              {Object.keys(crop.leaf_ranges ?? {}).length === 0 && ' · no leaf standards yet'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h4 className="text-sm font-semibold text-neutral-800">Nutrient removal</h4>
            <p className="text-xs text-neutral-500">What a tonne of harvested crop takes off. Times a planting's yield, it is that planting's maintenance amounts.</p>
          </div>
          <Button size="sm" variant="outline" isLoading={busy === 'research'} disabled={busy !== null} onClick={runResearch}>
            <Sparkles className="h-4 w-4" />
            Look up published figures
          </Button>
        </div>

        {busy === 'research' && <p className="text-sm text-neutral-600">Searching published sources. This can take a couple of minutes.</p>}

        {research && (
          <div className="rounded-lg border border-primary-200 bg-primary-50 p-3 space-y-2 text-sm">
            <p className="text-primary-900">
              {research.found
                ? 'Researched by AI: check each figure against its source before saving. Nothing is saved until you press Save.'
                : 'Nothing usable was found. Type the figures from your own source.'}
            </p>
            {research.basis && <p className="text-neutral-700"><strong>Basis:</strong> {research.basis}</p>}
            {research.notes && <p className="text-neutral-700">{research.notes}</p>}
            {research.warnings.length > 0 && (
              <ul className="list-disc pl-5 text-amber-800 text-xs space-y-0.5">{research.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
            )}
            {research.sources.length > 0 && (
              <ul className="text-xs space-y-0.5">
                {research.sources.map((s) => (
                  <li key={s.url} className="break-all">
                    <a href={s.url} target="_blank" rel="noreferrer" className="text-primary-700 hover:underline">{s.title}</a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {REMOVAL_NUTRIENTS.map((n) => (
            <label key={n.key} className="block text-sm">
              <span className="text-neutral-700">{n.label} <span className="text-neutral-500">({n.per}/t)</span></span>
              <input
                type="text"
                inputMode="decimal"
                className={inputCls}
                value={values[n.key]}
                onChange={(e) => setValues((v) => ({ ...v, [n.key]: e.target.value }))}
              />
            </label>
          ))}
        </div>

        <label className="block text-sm">
          <span className="text-neutral-700">Where the figures come from</span>
          <textarea
            className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
            rows={2}
            value={source}
            onChange={(e) => setSource(e.target.value)}
          />
        </label>

        {error && <p className="text-sm text-error-700">{error}</p>}
        {saved && <p className="text-sm text-green-700">Saved.</p>}
        <div className="flex justify-end">
          <Button variant="primary" size="sm" isLoading={busy === 'save'} disabled={busy !== null} onClick={save}>Save removal figures</Button>
        </div>

        <div className="border-t border-neutral-100 pt-4">
          <LeafStandardsEditor crop={crop} onSaved={onSaved} />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Crop nutrient removal: what a tonne of harvested fruit takes off, per crop
 * type the organisation grows. The organisation's own figures, typed or
 * looked up and reviewed. A planting's maintenance amounts are these times
 * its yield (MaintenancePanel).
 */
export default function NutrientRemoval() {
  const [params] = useSearchParams();
  const focus = Number(params.get('crop_type')) || null;
  const [crops, setCrops] = useState<CropRemoval[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    soilService.getRemoval()
      .then((r) => setCrops(r.success && r.data ? r.data : []))
      .catch((e) => setError(errorMessage(e, 'Could not load the crops')));
  }, []);

  const ordered = crops ? [...crops].sort((a, b) => Number(b.crop_type_id === focus) - Number(a.crop_type_id === focus)) : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Crop nutrient figures"
        subtitle="Removal per tonne, and leaf standards"
        icon={<Scale className="h-6 w-6 text-white" />}
        showBackButton
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        <p className="text-sm text-neutral-600">
          Removal is per tonne of fresh fruit (nuts in shell) as harvested: macro nutrients in kg, trace elements in g. Elemental P and K, not
          P₂O₅ or K₂O. A planting's maintenance amounts are these figures times its yield. Leaf standards are the adequate
          range in leaf tissue that a leaf sample from the crop is marked against.
        </p>
        {error && <p className="text-sm text-error-700">{error}</p>}
        {!ordered && !error && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>}
        {ordered && ordered.length === 0 && (
          <p className="text-sm text-neutral-500">No plantings have a cultivar with a crop type yet, so there is no crop to set figures for.</p>
        )}
        {ordered?.map((c) => (
          <CropCard
            key={c.crop_type_id}
            crop={c}
            open={c.crop_type_id === focus}
            onSaved={(next) => setCrops((list) => list?.map((x) => (x.crop_type_id === next.crop_type_id ? next : x)) ?? null)}
          />
        ))}
      </div>
    </div>
  );
}
